import { findWhere } from "underscore";
import { useMemo } from "react";

import {
  Layout,
  ReusableViewIDs,
  View,
} from "../../../../types/PageInterfaces";
import useGlobalLists from "../../../../api/api-hooks/cloud/useGlobalLists";
import AppEmptyState from "../../../../elements/AppEmptyState";
import NoCardsLayouts from "../PageViewCards/NoCardsLayouts";
import { Prefill } from "../../../../types/GlobalInterfaces";
import { Module } from "../../../../types/TenantInterfaces";
import { SharedViewModes } from "../../PageViewRenderer";
import { EMPTY_ID } from "../../../../utils/Constants";
import useGroupBySchema from "./useGroupBySchema";
import { GetGroupTitle } from "../GroupByTable";
import Board from "./Board";

export interface BoardLayout extends Layout {
  GroupBy: string;
  EmptyGroup: boolean;
  Order: Array<string>;
}

interface Props {
  currentModule: Module;
  foundLayout: BoardLayout;
  query: any;
  records: any[];
  recordsLoading: boolean;
  props: any;
  selectedRecords: { [key: number]: boolean };
  setSelectedRecords: (newObj: object) => void;
  setSelectedPrefill: React.Dispatch<React.SetStateAction<Prefill | null>>;
  toggleCreateRecordsDrawer: () => void;
  view: View;
  parentViewID?: string;
  viewMode: SharedViewModes;
  onOpenRecordName: any;
  reusableViews?: ReusableViewIDs;
}

const emptyLayouts: [] = [];

export default function PageViewBoard({
  currentModule,
  query,
  foundLayout,
  records,
  recordsLoading,
  selectedRecords,
  setSelectedRecords,
  setSelectedPrefill,
  toggleCreateRecordsDrawer,
  view,
  parentViewID,
  viewMode,
  onOpenRecordName,
  reusableViews,
}: Props) {
  const boardLayouts = view?.BoardLayouts ?? emptyLayouts;
  const groupBy = foundLayout?.GroupBy;

  // Schema of the group by field
  const groupByFieldSchema = useGroupBySchema(currentModule?.Fields, groupBy);
  const sourcesList = groupByFieldSchema?.SourcesList;

  // @ts-ignore
  const isGlobalList = sourcesList?.startsWith?.("Global.Lists.");
  const { globalLists, globalListsLoading } = useGlobalLists(!!isGlobalList);

  // The Groups to be used in the Board. Currently supports Global Lists, Selects, and References.
  const groups = useMemo(() => {
    if (isGlobalList && globalListsLoading) return [];

    const fieldType = groupByFieldSchema?.FieldType as string;
    const isSelect = ["SingleSelect", "MultiSelect"].includes(fieldType);
    const isReference = ["SingleReference", "MultiReference"].includes(
      fieldType
    );

    const fieldName = groupByFieldSchema?.Name || groupBy;

    // Handle Global Lists. (Single Select with Global Options)
    if (isGlobalList && !globalListsLoading && globalLists?.length) {
      const globalList: any = findWhere(globalLists ?? [], {
        APIName: (groupByFieldSchema?.SourcesList as any)?.split(".")[2],
      });

      if (!globalList?.Variables?.length) return [];

      if (Array.isArray(foundLayout?.Order) && foundLayout?.Order.length) {
        return foundLayout?.Order.flatMap((value) => {
          if (value === EMPTY_ID) {
            return [{ Name: `No ${fieldName}`, Value: null }];
          }

          const variable = globalList.Variables.find(
            (group: any) => group.Value === value
          );

          return variable ? [variable] : [];
        });
      }

      return globalList.Variables;
    }

    // Handle SingleSelect/MultiSelect Groups.
    if (isSelect) {
      const sourcesList: any = groupByFieldSchema?.SourcesList ?? [];

      if (foundLayout?.Order) {
        return foundLayout?.Order.map((selectValue) => {
          if (selectValue === EMPTY_ID) {
            return {
              Name: `No ${fieldName}`,
              Value: null,
            };
          }

          return sourcesList.find(
            (option: any) => option.Value === selectValue
          );
        });
      }

      if (foundLayout?.EmptyGroup) {
        return [...sourcesList, { Name: `No ${fieldName}`, Value: null }];
      }

      return sourcesList;
    }

    // Handle SingleReference & MultiReference Groups.
    let groupRecords = records?.map((r) => {
      // Location support
      const groupByKey = groupBy?.includes(".")
        ? groupBy.split(".")[0]
        : groupBy;
      const groupRecord = r?.GroupBy?.[groupByKey];

      let groupName;

      if (groupByFieldSchema?.FieldType === "Tags") {
        groupName = r?.GroupBy?.Name || r?.GroupBy?.[groupByKey];
      } else {
        groupName = GetGroupTitle(groupRecord, groupBy, currentModule);
      }

      return {
        Name: groupName,
        Value: groupRecord,
      };
    });

    if (Array.isArray(foundLayout?.Order) && foundLayout?.Order.length) {
      groupRecords = foundLayout?.Order?.flatMap((groupValue: string) => {
        if (groupValue === EMPTY_ID) {
          return [{ Name: `No ${fieldName}`, Value: null }];
        }

        const matchedGroup = groupRecords.find((group) => {
          if (isReference) {
            return group.Value?._RecordName === groupValue;
          }

          return group.Value === groupValue;
        });

        return matchedGroup ? [matchedGroup] : [];
      });
    }

    // If the layout has an empty group, filter out the empty group.
    if (!foundLayout?.EmptyGroup) {
      return groupRecords.filter(
        (item) => item.Value !== EMPTY_ID && item.Value !== null
      );
    }

    return groupRecords;
  }, [
    groupByFieldSchema,
    records,
    currentModule,
    isGlobalList,
    globalListsLoading,
    foundLayout?.EmptyGroup,
    foundLayout?.Order,
  ]);

  if (!boardLayouts?.length) {
    return (
      <NoCardsLayouts
        viewID={view?._id ?? ""}
        currentModule={currentModule}
        parentViewID={parentViewID}
        viewMode={viewMode}
      />
    );
  }

  const loading = (isGlobalList && globalListsLoading) || recordsLoading;

  if (!loading && (!Array.isArray(groups) || !groups.length)) {
    return (
      <AppEmptyState
        title="No Results"
        description="There are no records matching the current filter."
        icon="mdi-archive-search"
      />
    );
  }

  return (
    <Board
      onOpenRecordName={onOpenRecordName}
      records={records}
      recordsLoading={recordsLoading}
      currentModule={currentModule}
      selectedRecords={selectedRecords}
      setSelectedRecords={setSelectedRecords}
      groupBy={groupBy}
      groups={groups}
      loading={loading}
      query={query}
      groupByFieldSchema={groupByFieldSchema}
      defaultRecordLayout={view?.DefaultRecordLayout}
      setSelectedPrefill={setSelectedPrefill}
      toggleCreateRecordsDrawer={toggleCreateRecordsDrawer}
      view={view}
      reusableViews={reusableViews}
    />
  );
}
